import { ReactNode, createContext, useContext, useEffect, useRef } from 'react'
import { useCookies } from 'react-cookie'

import request from 'api'
import i18n from 'utils/i18n'

import { useExperiments } from '../hooks/use-experiment'
import { generateRandomString } from '../utils/common'

interface DeviceIdentificationContextValue {
  setSaDid: (requestParam: any) => Promise<string>
}

const DeviceIdentificationContext = createContext<DeviceIdentificationContextValue>({
  setSaDid: async () => {
    throw new Error('DeviceIdentificationProvider not found')
  }
})

type DeviceIdentificationProviderProps = {
  children: ReactNode
}

export const DeviceIdentificationProvider = ({ children }: DeviceIdentificationProviderProps) => {
  const [cookies, setCookie] = useCookies()
  const prevSaDidTemp = useRef('init')
  const { getDeviceIdentificationSupportedFlag } = useExperiments()
  const SADID_COOKIE = 'sa_did'
  const SADIDTEMP_COOKIE = 'sa_did_temp'
  const SEC_DAY_365 = 365 * 24 * 60 * 60

  useEffect(() => {
    if (cookies.sa_did === undefined) {
      const initializeSadid = async () => {
        await setSaDid({ saDid: '', prevValue: '', isLogRequired: false, isSaDidTemp: false })
      }
      initializeSadid()
    }

    if (cookies.sa_did_temp === undefined) {
      const initializeSadidTemp = async () => {
        const countryCode = i18n.language.split('-')[1] || ''
        const isSupportSaDidTemp = await getDeviceIdentificationSupportedFlag(countryCode)
        if (isSupportSaDidTemp) {
          prevSaDidTemp.current = await setSaDid({
            saDid: cookies.sa_did === undefined ? '' : cookies.sa_did.toString(),
            prevValue: prevSaDidTemp.current,
            isLogRequired: true,
            isSaDidTemp: true
          })
        }
      }
      initializeSadidTemp()
    }
  }, [cookies])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = `${process.env.REACT_APP_DEVICE_IDENTIFICATION_LIBRARY_URL}?v=${Date.now()}`
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }, [])

  const setSaDid = async (requestParam: any) => {
    let visitorId: string
    visitorId = await window.samsung.deviceIdentification.send(
      request,
      requestParam.saDid,
      requestParam.prevValue,
      requestParam.isLogRequired
    )
    let cookieKey = requestParam.isSaDidTemp ? SADIDTEMP_COOKIE : SADID_COOKIE
    let addRandomValueForSaDid = requestParam.isSaDidTemp ? '' : generateRandomString(8)

    setCookie(cookieKey, visitorId + addRandomValueForSaDid, {
      domain: `${process.env.REACT_APP_COOKIE_DOMAIN}`,
      path: '/',
      secure: true,
      maxAge: requestParam.isSaDidTemp ? undefined : SEC_DAY_365
    })

    return visitorId
  }

  return <DeviceIdentificationContext.Provider value={{ setSaDid }}>{children}</DeviceIdentificationContext.Provider>
}

export const useDeviceIdentification = () => {
  const context = useContext(DeviceIdentificationContext)
  if (!context) {
    throw new Error('useDeviceIdentification must be used within DeviceIdentificationProvider')
  }
  return context
}
